import { Link, useParams } from "react-router-dom";

import { Definition } from "../components/Definition";
import { EmptyState, Loading } from "../components/StateViews";
import { trpc } from "../trpc";

function DohaChip({ slug }: { slug: string }) {
  return (
    <Link
      to={`/doha/${slug}`}
      className="rounded-full border border-line bg-paper-raised px-3 py-1 font-mono text-xs text-ink-soft transition-colors hover:border-terracotta/50 hover:text-terracotta"
    >
      {slug}
    </Link>
  );
}

function WordChip({ slug, label }: { slug: string; label: string }) {
  return (
    <Link
      to={`/glossary/${slug}`}
      className="rounded-full border border-line bg-paper-raised px-3 py-1 font-serif text-sm text-ink transition-colors hover:border-terracotta/50 hover:text-terracotta"
    >
      {label}
    </Link>
  );
}

export function TermPage() {
  const { slug = "" } = useParams();
  const query = trpc.glossary.term.useQuery(
    { slug },
    { enabled: slug.length > 0 },
  );

  if (query.isLoading) return <Loading label="Opening the word" />;
  if (!query.data) {
    return (
      <div className="mx-auto max-w-3xl px-5 pt-20 sm:px-8">
        <EmptyState title="That word isn’t in the glossary yet.">
          <Link to="/glossary" className="text-terracotta hover:underline">
            Back to the glossary
          </Link>
        </EmptyState>
      </div>
    );
  }

  const { term, senses, dohas, relatedOut, relatedIn } = query.data;

  return (
    <div className="mx-auto max-w-5xl px-5 pt-14 sm:px-8">
      <Link
        to="/glossary"
        className="eyebrow transition-colors hover:text-terracotta"
      >
        ← Glossary
      </Link>

      <header className="mt-4 flex flex-wrap items-end justify-between gap-4 border-b border-line pb-6">
        <div>
          <h1 className="font-serif text-5xl text-ink">{term.displayName}</h1>
          {term.devanagari ? (
            <p className="mt-1 font-deva text-2xl text-ink-soft">
              {term.devanagari}
            </p>
          ) : null}
          <p className="mt-2 text-sm text-ink-faint">
            {senses.length} {senses.length === 1 ? "meaning" : "meanings"} ·
            appears in {dohas.length} {dohas.length === 1 ? "doha" : "dohas"}
          </p>
        </div>
        <Link
          to={`/graph?focus=${term.slug}`}
          className="rounded-full border border-line-strong px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-paper-sunk"
        >
          Focus in graph →
        </Link>
      </header>

      <div className="mt-8 grid gap-12 lg:grid-cols-[1fr_18rem]">
        <div>
          {term.isStub && senses.length === 0 ? (
            <EmptyState title="This word is referenced but not yet defined.">
              It’s named by other entries but has no meaning written down. A
              perfect candidate for the “needs a definition” list.
            </EmptyState>
          ) : (
            <ol className="space-y-7">
              {senses.map((sense, i) => (
                <li key={sense.id} className="flex gap-4">
                  <span className="mt-1 font-serif text-2xl text-terracotta/70">
                    {i + 1}
                  </span>
                  <div>
                    <p className="font-serif text-xl leading-relaxed text-ink">
                      <Definition text={sense.definition} />
                    </p>
                    {sense.dohaRefs.length > 0 ? (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {sense.dohaRefs.map((ref) => (
                          <DohaChip key={ref} slug={ref} />
                        ))}
                      </div>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>

        <aside className="space-y-8">
          {relatedOut.length > 0 ? (
            <section>
              <h2 className="eyebrow">This word calls on</h2>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {relatedOut.map((r) => (
                  <WordChip key={r.slug} slug={r.slug} label={r.displayName} />
                ))}
              </div>
            </section>
          ) : null}

          {relatedIn.length > 0 ? (
            <section>
              <h2 className="eyebrow">Called on by</h2>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {relatedIn.map((r) => (
                  <WordChip key={r.slug} slug={r.slug} label={r.displayName} />
                ))}
              </div>
            </section>
          ) : null}

          {dohas.length > 0 ? (
            <section>
              <h2 className="eyebrow">Appears in</h2>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {dohas.map((d) => (
                  <DohaChip key={d.slug} slug={d.slug} />
                ))}
              </div>
            </section>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
